import React, { isValidElement, forwardRef, useState, useRef, useEffect, useCallback, createElement, useMemo, useContext } from 'react';
import { createPortal } from 'react-dom';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}

var escapeRegExp = function (str) { return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }; // $& means the whole matched string
var ensureEndsWith = function (ending) { return function (str) {
    var _str = str.trim();
    var regex = new RegExp(".*?" + escapeRegExp(ending) + "$");
    return regex.test(_str) ? _str : "" + _str + ending;
}; };
var randomString = function () { return Math.random().toString(36).substring(7); };

var ComponentOrElement = function (_a) {
    var className = _a.className, Value = _a.value;
    return !Value ? null : isValidElement(Value) ? (React.cloneElement(Value, {
        className: ((Value.props.className || '') + " " + className).trim(),
    })) : (React.createElement(Value, { className: className }));
};
var applyProps = function (appliedProps, Component) {
    var AppliedComponent = function (props) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        var _appliedProps = typeof appliedProps === 'function' ? appliedProps(props) : appliedProps;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return React.createElement(Component, __assign({}, _appliedProps, props));
    };
    return AppliedComponent;
};
var applyPropsRef = function (appliedProps, Component) {
    var AppliedComponent = function (props, ref) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        var _appliedProps = typeof appliedProps === 'function' ? appliedProps(props) : appliedProps;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return React.createElement(Component, __assign({}, _appliedProps, props, { ref: ref }));
    };
    return forwardRef(AppliedComponent);
};

var ensureDash = ensureEndsWith('-');
var arrayModMapper = function (block) { return function (mod) { return block + "--" + mod; }; };
var mapModifiers = function (block, mods) {
    return Object.keys(mods)
        .filter(function (mod) { return !!mods[mod]; })
        .map(function (mod) {
        return typeof mods[mod] === 'string' || typeof mods[mod] === 'number' ? block + "--" + mod + "-" + mods[mod] : block + "--" + mod;
    });
};
var argMapper = function (block) { return function (arg) {
    return Array.isArray(arg) ? arg.map(arrayModMapper(block)) : mapModifiers(block, arg);
}; };
var removeFirst = function (args) {
    var result = __spreadArrays(args);
    // eslint-disable-next-line fp/no-mutating-methods
    result.shift();
    return result;
};
var handleBlockOrElement = function (block, args) {
    return args.length && typeof args[0] === 'string' && block ? [block + "__" + args[0], removeFirst(args)] : [block, args];
};
var configureBem = function (_a) {
    var _b = _a === void 0 ? {} : _a, prefix = _b.prefix, _c = _b.classes, classes = _c === void 0 ? {} : _c;
    return function (block) { return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var _a = handleBlockOrElement(block || '', args.filter(function (arg) { return !!arg; })), blockOrElement = _a[0], _args = _a[1];
        var modifiers = _args.filter(function (arg) { return typeof arg !== 'string'; });
        var classnames = _args.filter(function (arg) { return typeof arg === 'string'; });
        var prefixed = block
            ? __spreadArrays(["" + blockOrElement], modifiers.flatMap(argMapper(blockOrElement))).map(function (c) {
                return c in classes ? classes[c] : "" + (prefix ? ensureDash(prefix) : '') + c;
            })
            : [];
        return __spreadArrays(prefixed, classnames).join(' ');
    }; };
};
var bem = configureBem();

var e=[],t=[];function injector_0c888434(n,r){if(n&&"undefined"!=typeof document){var a,s=!0===r.prepend?"prepend":"append",d=!0===r.singleTag,i="string"==typeof r.container?document.querySelector(r.container):document.getElementsByTagName("head")[0];if(d){var u=e.indexOf(i);-1===u&&(u=e.push(i)-1,t[u]={}),a=t[u]&&t[u][s]?t[u][s]:t[u][s]=c();}else a=c();65279===n.charCodeAt(0)&&(n=n.substring(1)),a.styleSheet?a.styleSheet.cssText+=n:a.appendChild(document.createTextNode(n));}function c(){var e=document.createElement("style");if(e.setAttribute("type","text/css"),r.attributes)for(var t=Object.keys(r.attributes),n=0;n<t.length;n++)e.setAttribute(t[n],r.attributes[t[n]]);var a="prepend"===s?"afterbegin":"beforeend";return i.insertAdjacentElement(a,e),e}}

const css = "/* stylelint-disable scss/dollar-variable-pattern */\n/* stylelint-enable scss/dollar-variable-pattern */\n.gui-flex {\n  display: flex;\n  align-items: center;\n}\n.gui-flex--column {\n  flex-direction: column;\n  align-items: stretch;\n}\n.gui-flex--inline {\n  display: inline-flex;\n}";
injector_0c888434(css,{"container":"body","singleTag":true,"prepend":true});

var getOuterMargins = function (margin, direction) {
    return direction === 'row'
        ? {
            marginLeft: "-" + margin,
            marginRight: "-" + margin,
        }
        : {
            marginTop: "-" + margin,
            marginBottom: "-" + margin,
        };
};
var getMargins = function (margin, direction) {
    return direction === 'row'
        ? {
            marginLeft: "" + margin,
            marginRight: "" + margin,
        }
        : {
            marginTop: "" + margin,
            marginBottom: "" + margin,
        };
};
var configureFlex = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.prefix, prefix = _c === void 0 ? 'gui' : _c, classes = _b.classes;
    var bem = configureBem({ prefix: prefix, classes: classes });
    var b = bem('flex');
    var Flex = function (_a, ref) {
        var _b = _a.direction, direction = _b === void 0 ? 'row' : _b, justifyContent = _a.justifyContent, inline = _a.inline, children = _a.children, className = _a.className, _c = _a.style, style = _c === void 0 ? {} : _c, _d = _a.itemClassName, itemClassName = _d === void 0 ? '' : _d, margin = _a.margin;
        return (React.createElement("div", { ref: ref, className: b({ column: direction === 'col', inline: inline }, className), style: __assign(__assign(__assign({}, style), { justifyContent: justifyContent }), (margin ? getOuterMargins(margin, direction) : undefined)) }, React.Children.map(children, function (child) {
            return isValidElement(child) ? (child.props.flexIgnore ? (child) : (React.cloneElement(child, {
                className: (itemClassName + " " + (child.props.className || '')).trim(),
                style: __assign(__assign({}, (child.props.style || {})), (margin ? getMargins(margin, direction) : {})),
            }))) : child && child !== true ? (React.createElement("span", { className: itemClassName, style: __assign({}, (margin ? getMargins(margin, direction) : {})) }, child)) : (child);
        })));
    };
    return Flex;
};
var Flex = forwardRef(configureFlex());
var FlexIgnoreInternal = function (_a) {
    var children = _a.children;
    return React.createElement(React.Fragment, null, children);
};
var FlexIgnore = applyProps({ ignore: true }, FlexIgnoreInternal);

const css$1 = "/* stylelint-disable scss/dollar-variable-pattern */\n/* stylelint-enable scss/dollar-variable-pattern */\n.gui-spinner {\n  display: inline-block;\n  position: relative;\n}\n.gui-spinner div {\n  background-color: currentColor;\n  position: absolute;\n}\n.gui-spinner--size-small {\n  height: 1.5rem;\n  width: 1.5rem;\n}\n.gui-spinner--size-small div {\n  width: 0.1875rem;\n  height: 0.1875rem;\n  border-radius: 0.09375rem;\n  animation: gui-spinner 1.2s linear infinite;\n}\n.gui-spinner--size-small div:nth-child(1) {\n  animation-delay: 0s;\n  top: 0.984375rem;\n  left: 1.2245791712rem;\n}\n.gui-spinner--size-small div:nth-child(2) {\n  animation-delay: 0.1s;\n  top: 1.2245791712rem;\n  left: 0.984375rem;\n}\n.gui-spinner--size-small div:nth-child(3) {\n  animation-delay: 0.2s;\n  top: 1.3125rem;\n  left: 0.65625rem;\n}\n.gui-spinner--size-small div:nth-child(4) {\n  animation-delay: 0.3s;\n  top: 1.2245791712rem;\n  left: 0.328125rem;\n}\n.gui-spinner--size-small div:nth-child(5) {\n  animation-delay: 0.4s;\n  top: 0.984375rem;\n  left: 0.0879208288rem;\n}\n.gui-spinner--size-small div:nth-child(6) {\n  animation-delay: 0.5s;\n  top: 0.65625rem;\n  left: 0rem;\n}\n.gui-spinner--size-small div:nth-child(7) {\n  animation-delay: 0.6s;\n  top: 0.328125rem;\n  left: 0.0879208288rem;\n}\n.gui-spinner--size-small div:nth-child(8) {\n  animation-delay: 0.7s;\n  top: 0.0879208288rem;\n  left: 0.328125rem;\n}\n.gui-spinner--size-small div:nth-child(9) {\n  animation-delay: 0.8s;\n  top: 0rem;\n  left: 0.65625rem;\n}\n.gui-spinner--size-small div:nth-child(10) {\n  animation-delay: 0.9s;\n  top: 0.0879208288rem;\n  left: 0.984375rem;\n}\n.gui-spinner--size-small div:nth-child(11) {\n  animation-delay: 1s;\n  top: 0.328125rem;\n  left: 1.2245791712rem;\n}\n.gui-spinner--size-small div:nth-child(12) {\n  animation-delay: 1.1s;\n  top: 0.65625rem;\n  left: 1.3125rem;\n}\n.gui-spinner--size-large {\n  height: 3rem;\n  width: 3rem;\n}\n.gui-spinner--size-large div {\n  width: 0.25rem;\n  height: 0.25rem;\n  border-radius: 0.125rem;\n  animation: gui-spinner 1.2s linear infinite;\n}\n.gui-spinner--size-large div:nth-child(1) {\n  animation-delay: 0s;\n  top: 2.0625rem;\n  left: 2.5657849302rem;\n}\n.gui-spinner--size-large div:nth-child(2) {\n  animation-delay: 0.1s;\n  top: 2.5657849302rem;\n  left: 2.0625rem;\n}\n.gui-spinner--size-large div:nth-child(3) {\n  animation-delay: 0.2s;\n  top: 2.75rem;\n  left: 1.375rem;\n}\n.gui-spinner--size-large div:nth-child(4) {\n  animation-delay: 0.3s;\n  top: 2.5657849302rem;\n  left: 0.6875rem;\n}\n.gui-spinner--size-large div:nth-child(5) {\n  animation-delay: 0.4s;\n  top: 2.0625rem;\n  left: 0.1842150698rem;\n}\n.gui-spinner--size-large div:nth-child(6) {\n  animation-delay: 0.5s;\n  top: 1.375rem;\n  left: 0rem;\n}\n.gui-spinner--size-large div:nth-child(7) {\n  animation-delay: 0.6s;\n  top: 0.6875rem;\n  left: 0.1842150698rem;\n}\n.gui-spinner--size-large div:nth-child(8) {\n  animation-delay: 0.7s;\n  top: 0.1842150698rem;\n  left: 0.6875rem;\n}\n.gui-spinner--size-large div:nth-child(9) {\n  animation-delay: 0.8s;\n  top: 0rem;\n  left: 1.375rem;\n}\n.gui-spinner--size-large div:nth-child(10) {\n  animation-delay: 0.9s;\n  top: 0.1842150698rem;\n  left: 2.0625rem;\n}\n.gui-spinner--size-large div:nth-child(11) {\n  animation-delay: 1s;\n  top: 0.6875rem;\n  left: 2.5657849302rem;\n}\n.gui-spinner--size-large div:nth-child(12) {\n  animation-delay: 1.1s;\n  top: 1.375rem;\n  left: 2.75rem;\n}\n@keyframes gui-spinner {\n  0%, 20%, 80%, 100% {\n    transform: scale(1);\n  }\n  50% {\n    transform: scale(1.5);\n  }\n}";
injector_0c888434(css$1,{"container":"body","singleTag":true,"prepend":true});

var configureSpinner = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.prefix, prefix = _c === void 0 ? 'gui' : _c, classes = _b.classes;
    var bem = configureBem({ prefix: prefix, classes: classes });
    var b = bem('spinner');
    var Spinner = function (_a) {
        var className = _a.className, _b = _a.size, size = _b === void 0 ? 'small' : _b;
        return (React.createElement("div", { className: b({ size: size }, className) },
            React.createElement("div", null),
            React.createElement("div", null),
            React.createElement("div", null),
            React.createElement("div", null),
            React.createElement("div", null),
            React.createElement("div", null),
            React.createElement("div", null),
            React.createElement("div", null),
            React.createElement("div", null),
            React.createElement("div", null),
            React.createElement("div", null),
            React.createElement("div", null)));
    };
    return Spinner;
};
var Spinner = configureSpinner();

const css$2 = "/* stylelint-disable scss/dollar-variable-pattern */\n/* stylelint-enable scss/dollar-variable-pattern */\n.gui-button {\n  display: inline-block;\n  cursor: pointer;\n  background: none;\n  position: relative;\n  border: none;\n  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);\n  font-weight: 500;\n  font-size: 0.875rem;\n  min-height: 2.25rem;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  border-radius: 0.25rem;\n  padding: 0 0.75rem;\n}\n.gui-button--color-default {\n  background-color: #e0e0e0;\n  color: rgba(0, 0, 0, 0.87);\n}\n.gui-button--color-default:hover, .gui-button--color-default:focus {\n  background-color: lightgray;\n  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);\n}\n.gui-button--color-primary {\n  background-color: #1976d2;\n  color: #fff;\n}\n.gui-button--color-primary:hover, .gui-button--color-primary:focus {\n  background-color: #1669bb;\n  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);\n}\n.gui-button--color-secondary {\n  background-color: #dc004e;\n  color: #fff;\n}\n.gui-button--color-secondary:hover, .gui-button--color-secondary:focus {\n  background-color: #c30045;\n  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);\n}\n.gui-button--disabled {\n  background-color: rgba(0, 0, 0, 0.12);\n  color: rgba(0, 0, 0, 0.26);\n  box-shadow: none;\n  cursor: default;\n  pointer-events: none;\n}\n.gui-button--disabled:hover, .gui-button--disabled:focus {\n  background-color: rgba(0, 0, 0, 0.12);\n  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);\n}\n.gui-button--disabled:hover, .gui-button--disabled:focus {\n  box-shadow: none;\n}\n.gui-button--loading {\n  cursor: default;\n}\n.gui-button--loading:hover, .gui-button--loading:focus {\n  box-shadow: none;\n  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);\n}\n.gui-button--block {\n  display: block;\n  width: 100%;\n}\n.gui-button:focus {\n  outline: none;\n}\n.gui-button:active {\n  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);\n}\n.gui-button--color-clear {\n  box-shadow: none;\n}\n.gui-button--color-clear:active {\n  box-shadow: none;\n}\n.gui-button--color-clear:hover, .gui-button--color-clear:focus {\n  background-color: #e6e6e6;\n}\n.gui-button__icon-wrapper {\n  display: flex;\n  align-items: center;\n}\n.gui-button--icon {\n  border-radius: 1rem;\n}\n.gui-button__ripple {\n  position: absolute;\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  overflow: hidden;\n  pointer-events: none;\n  border-radius: inherit;\n}\n.gui-button__ripple-child {\n  position: absolute;\n  background-color: rgba(255, 255, 255, 0.2);\n  border-radius: 50%;\n  animation-duration: 550ms;\n  animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);\n  animation-delay: 0s;\n  animation-iteration-count: 1;\n  animation-direction: normal;\n  animation-fill-mode: none;\n  animation-play-state: running;\n  animation-name: ripple;\n}\n.gui-button__ripple-child--color-default {\n  background-color: rgba(97, 97, 97, 0.2);\n}\n@keyframes ripple {\n  0% {\n    transform: scale(0, 0);\n  }\n  100% {\n    transform: scale(1, 1);\n  }\n}";
injector_0c888434(css$2,{"container":"body","singleTag":true,"prepend":true});

var isNodeEmpty = function (node) { return !node || (Array.isArray(node) && !node.length); };
var configureButton = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.prefix, prefix = _c === void 0 ? 'gui' : _c, _d = _b.Spinner, Spinner$1 = _d === void 0 ? Spinner : _d, _e = _b.Flex, Flex$1 = _e === void 0 ? Flex : _e, classes = _b.classes;
    var bem = configureBem({ prefix: prefix, classes: classes });
    var b = bem('button');
    var ButtonInternal = function (_a, outsideRef) {
        var _b = _a.loading, loading = _b === void 0 ? false : _b, _c = _a.color, color = _c === void 0 ? 'default' : _c, _d = _a.block, block = _d === void 0 ? false : _d, _e = _a.disabled, disabled = _e === void 0 ? false : _e, className = _a.className, children = _a.children, IconLeft = _a.iconLeft, IconRight = _a.iconRight, _f = _a.style, style = _f === void 0 ? {} : _f, other = __rest(_a, ["loading", "color", "block", "disabled", "className", "children", "iconLeft", "iconRight", "style"]);
        var _g = useState(0), width = _g[0], setWidth = _g[1];
        var internalRef = useRef(null);
        var ref = outsideRef || internalRef;
        var rippleRef = useRef(null);
        var _h = useState(), rippleChild = _h[0], setRippleChild = _h[1];
        useEffect(function () {
            requestAnimationFrame(function () {
                if (typeof ref !== 'function' && ref && ref.current) {
                    setWidth(ref.current.offsetWidth);
                }
            });
        });
        var onMouseDown = useCallback(function (event) {
            if (typeof ref !== 'function' && ref && ref.current && rippleRef.current) {
                var element = ref.current;
                var clickX = event.clientX - element.getBoundingClientRect().left;
                var clickY = event.clientY - element.getBoundingClientRect().top;
                var centerX = element.offsetWidth / 2;
                var centerY = element.offsetHeight / 2;
                var size = Math.max(element.offsetWidth, element.offsetHeight) +
                    Math.max(Math.abs(clickX - centerX), Math.abs(clickY - centerY)) * 2 +
                    10;
                var x = clickX - size / 2;
                var y = clickY - size / 2;
                var newRipple_1 = document.createElement('div');
                // eslint-disable-next-line fp/no-mutation
                newRipple_1.className = b('ripple-child', { color: color });
                // eslint-disable-next-line fp/no-mutation
                newRipple_1.style.left = x + "px";
                // eslint-disable-next-line fp/no-mutation
                newRipple_1.style.top = y + "px";
                // eslint-disable-next-line fp/no-mutation
                newRipple_1.style.width = size + "px";
                // eslint-disable-next-line fp/no-mutation
                newRipple_1.style.height = size + "px";
                // eslint-disable-next-line fp/no-mutation
                newRipple_1.dataset.down = '1';
                // eslint-disable-next-line fp/no-mutation
                newRipple_1.dataset.animating = '1';
                setRippleChild(newRipple_1);
                rippleRef.current.appendChild(newRipple_1);
                setTimeout(function () {
                    // eslint-disable-next-line fp/no-mutation
                    newRipple_1.dataset.animating = '0';
                    if (newRipple_1.dataset.down !== '1') {
                        newRipple_1.remove();
                    }
                }, 550);
            }
        }, [ref, rippleRef, color, setRippleChild]);
        var onMouseUp = useCallback(function () {
            if (rippleChild) {
                // eslint-disable-next-line fp/no-mutation
                rippleChild.dataset.down = '0';
                if (rippleChild.dataset.animating !== '1') {
                    rippleChild.remove();
                }
            }
        }, [rippleChild]);
        var _style = width && loading
            ? __assign(__assign({}, style), { width: width + "px" }) : style;
        var onlyIcon = (IconLeft || IconRight) && isNodeEmpty(children);
        return (React.createElement("button", __assign({ ref: ref, style: _style, className: b({ color: color, disabled: disabled, loading: loading, block: block, icon: onlyIcon }, className), disabled: disabled || loading, onMouseLeave: onMouseUp, onMouseDown: onMouseDown, onMouseUp: onMouseUp }, other),
            loading ? (React.createElement(Spinner$1, null)) : (React.createElement(Flex$1, { margin: "0.25rem", justifyContent: block ? 'center' : 'flex-start' },
                IconLeft && (React.createElement("span", { className: b('icon-wrapper') },
                    React.createElement(ComponentOrElement, { value: IconLeft }))),
                !onlyIcon && React.createElement("span", null, children),
                IconRight && (React.createElement("span", { className: b('icon-wrapper') },
                    React.createElement(ComponentOrElement, { value: IconRight }))))),
            React.createElement("div", { ref: rippleRef, className: b('ripple') })));
    };
    return forwardRef(ButtonInternal);
};
var Button = configureButton();
var ClearButton = applyPropsRef({ color: 'clear' }, Button);
var PrimaryButton = applyPropsRef({ color: 'primary' }, Button);
var SecondaryButton = applyPropsRef({ color: 'secondary' }, Button);

const css$3 = "/* stylelint-disable scss/dollar-variable-pattern */\n/* stylelint-enable scss/dollar-variable-pattern */\n.gui-input {\n  border: 0.0625rem solid rgba(0, 0, 0, 0.87);\n  border-radius: 0.25rem;\n  background-color: #f5f5f5;\n  transition: background-color 550ms;\n  padding: 0 0.5rem;\n}\n.gui-input:focus {\n  outline: none;\n}\n.gui-input__inner {\n  padding: 0.5rem 0;\n  border: none;\n  width: calc(100% - 0.5rem * 2);\n  background-color: transparent;\n  min-height: 2.1875rem;\n}\n.gui-input__inner:active, .gui-input__inner:focus {\n  outline: none;\n  border: none;\n}\n.gui-input--focused {\n  border-color: #1976d2;\n  background-color: #fff;\n}\n.gui-input--iconLeft {\n  padding-left: 1rem;\n}\n.gui-input--iconRight {\n  padding-right: 1rem;\n}";
injector_0c888434(css$3,{"container":"body","singleTag":true,"prepend":true});

var configureInput = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.prefix, prefix = _c === void 0 ? 'gui' : _c, _d = _b.Flex, Flex$1 = _d === void 0 ? Flex : _d, classes = _b.classes;
    var bem = configureBem({ prefix: prefix, classes: classes });
    var b = bem('input');
    var Input = function (_a) {
        var iconLeft = _a.iconLeft, iconRight = _a.iconRight, onFocus = _a.onFocus, onBlur = _a.onBlur, className = _a.className, style = _a.style, other = __rest(_a, ["iconLeft", "iconRight", "onFocus", "onBlur", "className", "style"]);
        var _b = useState(false), focused = _b[0], setFocused = _b[1];
        var onInnerFocused = function (event) {
            onFocus && onFocus(event);
            setFocused(true);
        };
        var onInnerBlur = function (event) {
            onBlur && onBlur(event);
            setFocused(false);
        };
        return (React.createElement("div", { className: b({ iconLeft: !!iconLeft, iconRight: !!iconRight, focused: focused }, className), style: style },
            React.createElement(Flex$1, { margin: "0.5rem" },
                React.createElement(ComponentOrElement, { value: iconLeft }),
                React.createElement("input", __assign({ className: b('inner', { focused: focused }), onFocus: onInnerFocused, onBlur: onInnerBlur }, other)),
                React.createElement(ComponentOrElement, { value: iconRight }))));
    };
    return Input;
};
var Input = configureInput();

const css$4 = "/* stylelint-disable scss/dollar-variable-pattern */\n/* stylelint-enable scss/dollar-variable-pattern */\n.gui-icon--size-xsmall {\n  width: 0.5rem;\n  height: 0.5rem;\n}\n.gui-icon--size-small {\n  width: 1rem;\n  height: 1rem;\n}\n.gui-icon--size-medium {\n  width: 1.5rem;\n  height: 1.5rem;\n}\n.gui-icon--size-large {\n  width: 2rem;\n  height: 2rem;\n}\n.gui-icon--size-xlarge {\n  width: 3rem;\n  height: 3rem;\n}";
injector_0c888434(css$4,{"container":"body","singleTag":true,"prepend":true});

var configureIcon = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.prefix, prefix = _c === void 0 ? 'gui' : _c, classes = _b.classes;
    var bem = configureBem({ prefix: prefix, classes: classes });
    var b = bem('icon');
    var Icon = function (_a) {
        var style = _a.style, className = _a.className, _b = _a.size, size = _b === void 0 ? 'medium' : _b, SVG = _a.svg;
        return (React.createElement(SVG, { className: b({ size: size }, className), style: style }));
    };
    return Icon;
};
var Icon = configureIcon();

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

var _ref = /*#__PURE__*/createElement("path", {
  fill: "currentColor",
  d: "M143 352.3L7 216.3c-9.4-9.4-9.4-24.6 0-33.9l22.6-22.6c9.4-9.4 24.6-9.4 33.9 0l96.4 96.4 96.4-96.4c9.4-9.4 24.6-9.4 33.9 0l22.6 22.6c9.4 9.4 9.4 24.6 0 33.9l-136 136c-9.2 9.4-24.4 9.4-33.8 0z"
});

function SvgAngleDown(props) {
  return /*#__PURE__*/createElement("svg", _extends({
    "aria-hidden": "true",
    "data-prefix": "fas",
    "data-icon": "angle-down",
    viewBox: "0 0 320 512"
  }, props), _ref);
}

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

var _ref$1 = /*#__PURE__*/createElement("path", {
  fill: "currentColor",
  d: "M511.988 288.9c-.478 17.43-15.217 31.1-32.653 31.1H424v16c0 21.864-4.882 42.584-13.6 61.145l60.228 60.228c12.496 12.497 12.496 32.758 0 45.255-12.498 12.497-32.759 12.496-45.256 0l-54.736-54.736C345.886 467.965 314.351 480 280 480V236c0-6.627-5.373-12-12-12h-24c-6.627 0-12 5.373-12 12v244c-34.351 0-65.886-12.035-90.636-32.108l-54.736 54.736c-12.498 12.497-32.759 12.496-45.256 0-12.496-12.497-12.496-32.758 0-45.255l60.228-60.228C92.882 378.584 88 357.864 88 336v-16H32.666C15.23 320 .491 306.33.013 288.9-.484 270.816 14.028 256 32 256h56v-58.745l-46.628-46.628c-12.496-12.497-12.496-32.758 0-45.255 12.498-12.497 32.758-12.497 45.256 0L141.255 160h229.489l54.627-54.627c12.498-12.497 32.758-12.497 45.256 0 12.496 12.497 12.496 32.758 0 45.255L424 197.255V256h56c17.972 0 32.484 14.816 31.988 32.9zM257 0c-61.856 0-112 50.144-112 112h224C369 50.144 318.856 0 257 0z"
});

function SvgBug(props) {
  return /*#__PURE__*/createElement("svg", _extends$1({
    "aria-hidden": "true",
    "data-prefix": "fas",
    "data-icon": "bug",
    viewBox: "0 0 512 512"
  }, props), _ref$1);
}

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

var _ref$2 = /*#__PURE__*/createElement("path", {
  fill: "currentColor",
  d: "M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"
});

function SvgChevronLeft(props) {
  return /*#__PURE__*/createElement("svg", _extends$2({
    "aria-hidden": "true",
    "data-prefix": "fas",
    "data-icon": "chevron-left",
    viewBox: "0 0 320 512"
  }, props), _ref$2);
}

function _extends$3() { _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }

var _ref$3 = /*#__PURE__*/createElement("path", {
  fill: "currentColor",
  d: "M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
});

function SvgChevronRight(props) {
  return /*#__PURE__*/createElement("svg", _extends$3({
    "aria-hidden": "true",
    "data-prefix": "fas",
    "data-icon": "chevron-right",
    viewBox: "0 0 320 512"
  }, props), _ref$3);
}

function _extends$4() { _extends$4 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }

var _ref$4 = /*#__PURE__*/createElement("path", {
  fill: "currentColor",
  d: "M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"
});

function SvgFilter(props) {
  return /*#__PURE__*/createElement("svg", _extends$4({
    viewBox: "0 0 24 24"
  }, props), _ref$4);
}

function _extends$5() { _extends$5 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }

var _ref$5 = /*#__PURE__*/createElement("path", {
  fill: "currentColor",
  d: "M41 288h238c21.4 0 32.1 25.9 17 41L177 448c-9.4 9.4-24.6 9.4-33.9 0L24 329c-15.1-15.1-4.4-41 17-41zm255-105L177 64c-9.4-9.4-24.6-9.4-33.9 0L24 183c-15.1 15.1-4.4 41 17 41h238c21.4 0 32.1-25.9 17-41z"
});

function SvgSort(props) {
  return /*#__PURE__*/createElement("svg", _extends$5({
    "aria-hidden": "true",
    "data-prefix": "fas",
    "data-icon": "sort",
    viewBox: "0 0 320 512"
  }, props), _ref$5);
}

function _extends$6() { _extends$6 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6.apply(this, arguments); }

var _ref$6 = /*#__PURE__*/createElement("path", {
  fill: "currentColor",
  d: "M279 224H41c-21.4 0-32.1-25.9-17-41L143 64c9.4-9.4 24.6-9.4 33.9 0l119 119c15.2 15.1 4.5 41-16.9 41z"
});

function SvgSortAsc(props) {
  return /*#__PURE__*/createElement("svg", _extends$6({
    "aria-hidden": "true",
    "data-prefix": "fas",
    "data-icon": "sort-up",
    viewBox: "0 0 320 512"
  }, props), _ref$6);
}

function _extends$7() { _extends$7 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7.apply(this, arguments); }

var _ref$7 = /*#__PURE__*/createElement("path", {
  fill: "currentColor",
  d: "M41 288h238c21.4 0 32.1 25.9 17 41L177 448c-9.4 9.4-24.6 9.4-33.9 0L24 329c-15.1-15.1-4.4-41 17-41z"
});

function SvgSortDesc(props) {
  return /*#__PURE__*/createElement("svg", _extends$7({
    "aria-hidden": "true",
    "data-prefix": "fas",
    "data-icon": "sort-down",
    viewBox: "0 0 320 512"
  }, props), _ref$7);
}

var ChevronLeftIcon = applyProps({ svg: SvgChevronLeft }, Icon);
var ChevronRightIcon = applyProps({ svg: SvgChevronRight }, Icon);
var FilterIcon = applyProps({ svg: SvgFilter }, Icon);
var SortIcon = applyProps({ svg: SvgSort }, Icon);
var SortAscIcon = applyProps({ svg: SvgSortAsc }, Icon);
var SortDescIcon = applyProps({ svg: SvgSortDesc }, Icon);
var BugIcon = applyProps({ svg: SvgBug }, Icon);
var AngleDownIcon = applyProps({ svg: SvgAngleDown }, Icon);

const css$5 = "/* stylelint-disable scss/dollar-variable-pattern */\n/* stylelint-enable scss/dollar-variable-pattern */\n.gui-panel {\n  border-radius: 1rem;\n  background-color: #fff;\n  padding: 1rem;\n}\n.gui-panel--elevation-1 {\n  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);\n}\n.gui-panel--elevation-2 {\n  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);\n}\n.gui-panel--elevation-3 {\n  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23);\n}";
injector_0c888434(css$5,{"container":"body","singleTag":true,"prepend":true});

var configurePanel = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.prefix, prefix = _c === void 0 ? 'gui' : _c, classes = _b.classes;
    var bem = configureBem({ prefix: prefix, classes: classes });
    var b = bem('panel');
    var Panel = function (_a) {
        var className = _a.className, children = _a.children, _b = _a.elevation, elevation = _b === void 0 ? 1 : _b, other = __rest(_a, ["className", "children", "elevation"]);
        return (React.createElement("div", __assign({ className: b({ elevation: elevation }, className) }, other), children));
    };
    return Panel;
};
var Panel = configurePanel();

// eslint-disable-next-line no-shadow
var State;
(function (State) {
    State[State["inactive"] = 0] = "inactive";
    State[State["activating"] = 1] = "activating";
    State[State["active"] = 2] = "active";
    State[State["deactivating"] = 3] = "deactivating";
})(State || (State = {}));
var Transition = function (_a) {
    var on = _a.on, className = _a.className, _b = _a.duration, duration = _b === void 0 ? 0 : _b, children = _a.children, onMount = _a.onMount, onUnmount = _a.onUnmount;
    var _c = useState(State.inactive), state = _c[0], setState = _c[1];
    useEffect(function () {
        if (on) {
            if (state === State.inactive) {
                onMount && onMount();
                setState(State.activating);
                setTimeout(function () { return setState(State.active); }, duration);
            }
        }
        else if (state === State.active) {
            setState(State.deactivating);
            setTimeout(function () {
                setState(State.inactive);
                onUnmount && onUnmount();
            }, duration);
        }
    }, [on, duration, state, setState, onMount, onUnmount]);
    var b = bem(className);
    return (React.createElement(React.Fragment, null, state !== State.inactive
        ? React.Children.map(children, function (child) {
            return child
                ? React.cloneElement(child, {
                    className: b({ off: true, on: state === State.active }, child.props.className),
                })
                : child;
        })
        : null));
};

const css$6 = "/* stylelint-disable scss/dollar-variable-pattern */\n/* stylelint-enable scss/dollar-variable-pattern */\n.gui-popover {\n  position: absolute;\n  z-index: 900;\n}\n.gui-popover--off {\n  opacity: 0;\n  transition: opacity 0.15s linear, transform 0.15s linear;\n}\n.gui-popover--on {\n  opacity: 1;\n}\n.gui-popover__arrow {\n  position: absolute;\n  transform: rotate(45deg);\n  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);\n  background-color: white;\n  z-index: 800;\n}";
injector_0c888434(css$6,{"container":"body","singleTag":true,"prepend":true});

/* eslint-disable fp/no-mutation, no-param-reassign */
var DEFAULT_MARGIN = 12;
var ARROW_SHADOW_ID = '__arrow-shadow__';
var isInHierachy = function (element, someParent) {
    return element === someParent || (!!element.parentElement && isInHierachy(element.parentElement, someParent));
};
var isDomElement = function (eventTarget) {
    return !!eventTarget && (eventTarget instanceof HTMLElement || eventTarget instanceof SVGElement);
}; // eslint-disable-line no-prototype-builtins
var offset = function (el) {
    var rect = el.getBoundingClientRect();
    var scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return [rect.top + scrollTop, rect.left + scrollLeft];
};
var findScrollParent = function (element) {
    if (!element || element === document.body.parentElement) {
        return document.documentElement;
    }
    var style = getComputedStyle(element);
    return style.overflow === 'scroll' || style.overflow === 'auto' ? element : findScrollParent(element.parentElement);
};
var VERTICAL = {
    '': [0.5, -0.5, 0],
    left: [0, 0, 0],
    right: [1, -1, 0],
    top: [0, -1, -1],
    bottom: [1, 0, 1],
};
var HORIZONTAL = {
    '': [0.5, -0.5, 0],
    left: [0, -1, -1],
    right: [1, 0, 1],
    top: [0, 0, 0],
    bottom: [1, -1, 0],
};
var FACTOR_MATRIX = {
    bottom: VERTICAL,
    top: VERTICAL,
    left: HORIZONTAL,
    right: HORIZONTAL,
};
var translate = function (elementA) { return function (elementB) { return function (position) {
    var deltaA = offset(elementA);
    var deltaB = offset(elementB);
    return [position[0] + (deltaA[0] - deltaB[0]), position[1] + (deltaA[1] - deltaB[1])];
}; }; };
var subtractScoll = function (position) {
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    var scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    return [position[0] - scrollTop, position[1] - scrollLeft];
};
var addScroll = function (position) {
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    var scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    return [position[0] + scrollTop, position[1] + scrollLeft];
};
var positionCalc = function (referenceEl, element, margin, primaryPos, secondaryPos) {
    if (secondaryPos === void 0) { secondaryPos = ''; }
    var html = document.body.parentElement || document.body;
    var isHorizontal = ['top', 'bottom'].includes(primaryPos);
    var topFactor = FACTOR_MATRIX[primaryPos][isHorizontal ? primaryPos : secondaryPos];
    var leftFactor = FACTOR_MATRIX[primaryPos][isHorizontal ? secondaryPos : primaryPos];
    var top = referenceEl.offsetTop + topFactor[0] * referenceEl.offsetHeight + topFactor[1] * element.offsetHeight + topFactor[2] * margin;
    var left = referenceEl.offsetLeft +
        leftFactor[0] * referenceEl.offsetWidth +
        leftFactor[1] * element.offsetWidth +
        leftFactor[2] * margin;
    var pos = [top, left];
    var offsetParent = referenceEl.offsetParent || html;
    var scrollParent = findScrollParent(referenceEl);
    var scrollRect = scrollParent.getBoundingClientRect();
    var translatedPos = subtractScoll(translate(offsetParent)(scrollParent)(pos));
    var horizontalAdjust = function () {
        var scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        var refRect = referenceEl.getBoundingClientRect();
        var translatedAdjustedPos = [
            translatedPos[0],
            Math.min(refRect.left, Math.max(0, refRect.left + refRect.width - element.offsetWidth, Math.min(translatedPos[1], scrollRect.left + scrollRect.width - element.offsetWidth + scrollLeft))),
        ];
        return translate(scrollParent)(offsetParent)(addScroll(translatedAdjustedPos));
    };
    var verticalAdjust = function () {
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        var refRect = referenceEl.getBoundingClientRect();
        var translatedAdjustedPos = [
            Math.min(refRect.top, Math.max(0, refRect.top + refRect.height - element.offsetHeight, Math.min(translatedPos[0], scrollRect.top + window.innerHeight - element.offsetHeight + scrollTop))),
            translatedPos[1],
        ];
        return translate(scrollParent)(offsetParent)(addScroll(translatedAdjustedPos));
    };
    return isHorizontal ? horizontalAdjust() : verticalAdjust();
};
var ARROW_TOP_FACTORS = {
    top: [0, -1, -2],
    left: [0.5, 0, 0],
    right: [0.5, 0, 0],
    bottom: [1, 1, 2],
};
var ARROW_LEFT_FACTORS = {
    top: [0.5, 0, 0],
    left: [0, -1, -2],
    right: [1, 1, 2],
    bottom: [0.5, 0, 0],
};
var getArrowWidth = function (margin) { return Math.min(Math.sqrt(Math.pow((2 * margin), 2) / 2), 14); };
var arrowPositionCalc = function (element, primaryPos, margin) {
    var width = getArrowWidth(margin);
    var fT = ARROW_TOP_FACTORS[primaryPos];
    var fL = ARROW_LEFT_FACTORS[primaryPos];
    var top = element.offsetTop + fT[0] * element.offsetHeight + fT[1] * margin - 0.5 * width + fT[2];
    var left = element.offsetLeft + fL[0] * element.offsetWidth + fL[1] * margin - 0.5 * width + fL[2];
    return [top, left];
};
var splitPosition = function (position) {
    var match = /(\w+)(?:-(\w+))?/.exec(position);
    if (match) {
        return [match[1], match[2]];
    }
    throw new Error("invalid position: " + position);
};
var removeOldShadow = function () {
    var el = document.getElementById(ARROW_SHADOW_ID);
    if (el) {
        el.remove();
    }
};
// eslint-disable-next-line fp/no-let
var ticking = false;
var configurePopover = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.prefix, prefix = _c === void 0 ? 'gui' : _c, _d = _b.transitionDuration, transitionDuration = _d === void 0 ? 150 : _d, classes = _b.classes;
    // eslint-disable-next-line complexity
    var Popover = function (_a) {
        var open = _a.open, className = _a.className, onClose = _a.onClose, targetOrId = _a.target, _b = _a.useBody, useBody = _b === void 0 ? false : _b, children = _a.children, _c = _a.position, position = _c === void 0 ? 'bottom' : _c, _d = _a.margin, margin = _d === void 0 ? DEFAULT_MARGIN : _d, _e = _a.arrow, arrow = _e === void 0 ? true : _e;
        var target = typeof targetOrId === 'string' ? document.getElementById(targetOrId) : targetOrId;
        var element = useRef(null);
        var arrowRef = useRef(null);
        var body = document.body;
        var html = body.parentElement || body;
        // eslint-disable-next-line react-hooks/exhaustive-deps
        var translateToOffsetParent = useCallback(translate((target === null || target === void 0 ? void 0 : target.offsetParent) || html), [target]);
        var positionElement = useCallback(function () {
            requestAnimationFrame(function () {
                if (open && target && element.current) {
                    var _a = splitPosition(position), primaryPos = _a[0], secondaryPos = _a[1];
                    var originPos = positionCalc(target, element.current, margin, primaryPos, secondaryPos);
                    var translatedPos = useBody ? translateToOffsetParent(html)(originPos) : originPos;
                    element.current.style.top = translatedPos[0] + "px";
                    element.current.style.left = translatedPos[1] + "px";
                }
            });
        }, [html, target, element, position, open, margin, translateToOffsetParent, useBody]);
        var positionArrow = useCallback(function () {
            requestAnimationFrame(function () {
                if (open && target && element.current && margin && arrow && arrowRef.current) {
                    var primaryPos = splitPosition(position)[0];
                    var width = getArrowWidth(margin);
                    var _a = translateToOffsetParent(element.current)(arrowPositionCalc(target, primaryPos, margin)), arrowTop = _a[0], arrowLeft = _a[1];
                    var arrowElement = arrowRef.current;
                    var boxShadow = getComputedStyle(arrowElement).boxShadow;
                    arrowElement.dataset.originalBoxShadow = arrowElement.dataset.originalBoxShadow || boxShadow;
                    arrowElement.style.boxShadow = 'none';
                    arrowElement.style.top = arrowTop + "px";
                    arrowElement.style.left = arrowLeft + "px";
                    arrowElement.style.width = width + "px";
                    arrowElement.style.height = width + "px";
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    var arrowShadow = arrowElement.cloneNode();
                    arrowShadow.id = ARROW_SHADOW_ID;
                    arrowShadow.style.boxShadow = arrowElement.dataset.originalBoxShadow;
                    arrowShadow.style.zIndex = '-1';
                    removeOldShadow();
                    element.current.insertBefore(arrowShadow, element.current.children[0]);
                }
            });
        }, [target, element, arrow, margin, open, position, translateToOffsetParent]);
        var repositionListener = useCallback(function () {
            if (!ticking) {
                requestAnimationFrame(function () {
                    positionElement();
                    positionArrow();
                    ticking = false;
                });
                ticking = true;
            }
        }, [positionArrow, positionElement]);
        useEffect(function () {
            var scrollParent = findScrollParent(target);
            scrollParent.addEventListener('scroll', repositionListener);
            window.addEventListener('scroll', repositionListener);
            return function () {
                window.removeEventListener('scroll', repositionListener);
                scrollParent.removeEventListener('scroll', repositionListener);
            };
        }, [target, repositionListener]);
        useEffect(function () {
            window.addEventListener('resize', repositionListener);
            return function () {
                window.removeEventListener('resize', repositionListener);
            };
        }, [repositionListener]);
        useEffect(function () { return positionElement(); }, [positionElement]);
        useEffect(function () { return positionArrow(); }, [positionArrow]);
        // this makes it so that the popover is closed whenever something is clicked without preventing the intended click
        // action, but if the button itself is clicked again it the click action has to be prevented, otherwise the popover
        // would stay open
        var close = useCallback(function (event) {
            if (isDomElement(event.target) && isInHierachy(event.target, element.current)) {
                return true;
            }
            onClose();
            if (isDomElement(event.target) && isInHierachy(event.target, target)) {
                event.stopPropagation();
                return false;
            }
            return true;
        }, [onClose, target]);
        useEffect(function () {
            if (!open) {
                window.removeEventListener('click', close, { capture: true });
            }
            else {
                window.addEventListener('click', close, { capture: true });
            }
            return function () {
                window.removeEventListener('click', close, { capture: true });
            };
        }, [open, close]);
        if (!target) {
            return null;
        }
        var bem = configureBem({ prefix: prefix, classes: classes });
        var b = bem('popover');
        var container = useBody ? body : target.parentElement || body;
        return createPortal(React.createElement(Transition, { on: open, duration: transitionDuration, className: b() },
            React.createElement("div", { ref: element, className: b([], className) },
                children,
                arrow && React.createElement("div", { ref: arrowRef, className: b('arrow') }))), container);
    };
    return Popover;
};
var Popover = configurePopover();

const css$7 = "/* stylelint-disable scss/dollar-variable-pattern */\n/* stylelint-enable scss/dollar-variable-pattern */\n.gui-select {\n  border: 0.0625rem solid rgba(0, 0, 0, 0.87);\n  border-radius: 0.25rem;\n  background-color: #f5f5f5;\n  transition: background-color 550ms;\n  padding: 0 0.5rem;\n  cursor: pointer;\n}\n.gui-select:focus {\n  outline: none;\n}\n.gui-select__inner {\n  padding: 0.5rem 0;\n  border: none;\n  width: calc(100% - 0.5rem * 2);\n  background-color: transparent;\n  min-height: 2.1875rem;\n}\n.gui-select__inner:active, .gui-select__inner:focus {\n  outline: none;\n  border: none;\n}\n.gui-select--focused {\n  border-color: #1976d2;\n  background-color: #fff;\n}\n.gui-select--iconLeft {\n  padding-left: 1rem;\n}\n.gui-select--iconRight {\n  padding-right: 1rem;\n}\n.gui-select__inner {\n  padding: 0.5rem 0;\n  border: none;\n  width: calc(100% - 0.5rem * 2);\n  background-color: transparent;\n  min-height: 2.1875rem;\n  cursor: pointer;\n}\n.gui-select__inner:active, .gui-select__inner:focus {\n  outline: none;\n  border: none;\n}\n.gui-select__wrapper {\n  position: relative;\n}\n.gui-select__wrapper .gui-select__options {\n  border-radius: 0.25rem;\n  padding: 0.5rem 0;\n}\n.gui-select__options-list {\n  list-style: none;\n  margin: 0;\n  padding: 0;\n  max-height: 30rem;\n  overflow-y: auto;\n}\n.gui-select__options-entry {\n  padding: 0 0.5rem;\n  display: flex;\n  align-items: center;\n  min-height: 2.25rem;\n}\n.gui-select__options-entry--focus, .gui-select__options-entry:focus {\n  outline: none;\n  background-color: #f5f5f5;\n}\n.gui-select__options-entry--selected {\n  background-color: #1976d2;\n  color: #fff;\n}\n.gui-select__value {\n  position: absolute;\n}\n.gui-select__angle {\n  transition: transform 250ms ease-in-out;\n}\n.gui-select__angle--open {\n  transform: rotate(180deg);\n}";
injector_0c888434(css$7,{"container":"body","singleTag":true,"prepend":true});

var findOptionIndex = function (options, value) {
    var index = options.findIndex(function (option) { return option.value === value.value; });
    return index !== -1 ? index : 0;
};
var optionSearchFilter = function (searchValue) { return function (option) { var _a; return new RegExp(".*" + searchValue + ".*", 'i').test(((_a = option.text) === null || _a === void 0 ? void 0 : _a.toString()) || ''); }; };
var isInView = function (element, container) {
    var docViewTop = container.scrollTop;
    var docViewBottom = docViewTop + container.clientHeight;
    var elemTop = element.offsetTop;
    var elemBottom = elemTop + element.clientHeight;
    return elemBottom <= docViewBottom && elemTop >= docViewTop;
};
var scrollIntoView = function (listElement, index, top) {
    if (top === void 0) { top = false; }
    if (listElement) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        var element = listElement.children[index];
        if (!isInView(element, listElement)) {
            element.scrollIntoView(top);
            return true;
        }
    }
    return false;
};
var focusInput = function (inputElement) {
    if (inputElement) {
        inputElement.focus();
        return true;
    }
    return false;
};
var configureSelect = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.prefix, prefix = _c === void 0 ? 'gui' : _c, _d = _b.Flex, Flex$1 = _d === void 0 ? Flex : _d, _e = _b.Popover, Popover$1 = _e === void 0 ? Popover : _e, _f = _b.Panel, Panel$1 = _f === void 0 ? Panel : _f, classes = _b.classes;
    var bem = configureBem({ prefix: prefix, classes: classes });
    var b = bem('select');
    var Select = function (_a) {
        var _b;
        var options = _a.options, iconLeft = _a.iconLeft, className = _a.className, style = _a.style, onChange = _a.onChange, onFocus = _a.onFocus, onBlur = _a.onBlur, value = _a.value;
        var _c = useState(0), focusIndex = _c[0], setFocusIndex = _c[1];
        var _d = useState(''), search = _d[0], setSearch = _d[1];
        var _e = useState(false), focused = _e[0], setFocused = _e[1];
        var _f = useState(options), searchedOptions = _f[0], setSearchedOptions = _f[1];
        var _g = useState(false), open = _g[0], setOpen = _g[1];
        var target = useRef(null);
        var listRef = useRef(null);
        var inputRef = useRef(null);
        var onInnerFocused = function (event) {
            onFocus && onFocus(event);
            setFocused(true);
        };
        var onInnerBlur = function (event) {
            onBlur && onBlur(event);
            setFocused(false);
        };
        var focusListElement = useCallback(function (index, focusOptions) {
            var _index = Math.max(0, Math.min(focusOptions.length - 1, index));
            setFocusIndex(_index);
            return _index;
        }, [setFocusIndex]);
        var toggleOpen = useCallback(function () {
            if (open) {
                setOpen(false);
            }
            else {
                setOpen(true);
                setSearchedOptions(options);
                focusInput(inputRef.current);
                setTimeout(function () {
                    var selectedIndex = findOptionIndex(options, value);
                    var focusedIndex = focusListElement(selectedIndex, options);
                    scrollIntoView(listRef.current, focusedIndex);
                }, 0);
            }
        }, [open, inputRef, value, focusListElement, setSearchedOptions, options]);
        var setOption = useCallback(function (option) { return function () {
            onChange(option);
            setSearch('');
            setOpen(false);
            focusInput(inputRef.current);
        }; }, [onChange, inputRef, setOpen]);
        var onKeydown = useCallback(function (event) {
            var option = searchedOptions[focusIndex];
            switch (event.keyCode) {
                case 9:
                    setOption(option)();
                    break;
                case 13:
                    setOption(option)();
                    break;
                case 27:
                    if (open) {
                        toggleOpen();
                    }
                    break;
                case 32:
                    if (open) {
                        setOption(option)();
                    }
                    else {
                        toggleOpen();
                    }
                    event.preventDefault();
                    break;
                case 40:
                    scrollIntoView(listRef.current, focusListElement(focusIndex + 1, searchedOptions), false);
                    break;
                case 38:
                    scrollIntoView(listRef.current, focusListElement(focusIndex - 1, searchedOptions), true);
                    break;
            }
        }, [open, toggleOpen, searchedOptions, setOption, focusIndex, focusListElement]);
        var onOptionMouseOver = function (option) { return function () {
            var index = findOptionIndex(searchedOptions, option);
            focusListElement(index, searchedOptions);
        }; };
        var onSetSearch = useCallback(function (event) {
            setOpen(true);
            var searchValue = event.currentTarget.value;
            var newSearchOptions = options.filter(optionSearchFilter(searchValue));
            setSearchedOptions(newSearchOptions);
            setSearch(searchValue);
            focusListElement(findOptionIndex(newSearchOptions, value), newSearchOptions);
        }, [value, setOpen, options, setSearchedOptions, focusListElement]);
        var listBoxId = useMemo(function () { return "listbox-" + randomString(); }, []);
        var activeId = useMemo(function () { return "active-" + randomString(); }, []);
        var valuePosLeft = inputRef.current ? inputRef.current.offsetLeft : 0;
        return (React.createElement("div", { className: b('wrapper') },
            React.createElement("div", { onKeyDown: onKeydown, onFocus: onInnerFocused, onBlur: onInnerBlur, onClick: toggleOpen, tabIndex: 0, "aria-expanded": open, "aria-owns": listBoxId, 
                // this seems to be outdated, see https://www.w3.org/TR/wai-aria-1.1/#combobox
                // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
                role: "combobox", className: b({ iconLeft: !!iconLeft, focused: focused || open }, className), style: style, ref: target },
                React.createElement(Flex$1, { margin: "0.5rem" },
                    React.createElement(ComponentOrElement, { value: iconLeft }),
                    React.createElement("input", { ref: inputRef, role: "searchbox", "aria-autocomplete": "list", "aria-activedescendant": activeId, className: b('inner', { focused: focused || open }), value: search, onChange: onSetSearch }),
                    React.createElement(AngleDownIcon, { className: b('angle', { open: open }) }),
                    !search && (React.createElement(FlexIgnore, null,
                        React.createElement("div", { style: { left: valuePosLeft }, className: b('value') }, value.text || value.label || value.value))))),
            React.createElement(Popover$1, { arrow: false, target: target.current, open: open, onClose: function () { return setOpen(false); } },
                React.createElement(Panel$1, { style: { width: (_b = target.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().width }, className: b('options') },
                    React.createElement("ul", { id: listBoxId, role: "listbox", ref: listRef, className: b('options-list') }, searchedOptions.map(function (option, index) { return (React.createElement("li", { id: option.value === value.value ? activeId : undefined, role: "option", "aria-selected": option.value === value.value, className: b('options-entry', { focus: focusIndex === index, selected: option.value === value.value }), key: option.value, onClick: setOption(option), onFocus: function () { return setFocusIndex(index); }, onKeyDown: function () { return false; }, onMouseOver: onOptionMouseOver(option) }, option.label || option.text || option.value)); }))))));
    };
    return Select;
};
var Select = configureSelect();

const css$8 = "/* stylelint-disable scss/dollar-variable-pattern */\n/* stylelint-enable scss/dollar-variable-pattern */\n.gui-modal__container {\n  overflow-x: hidden;\n  overflow-y: auto;\n  position: fixed;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n  z-index: 1000;\n}\n.gui-modal__backdrop {\n  position: fixed;\n  top: 0;\n  left: 0;\n  width: 100vw;\n  height: 100vh;\n  z-index: 900;\n  background-color: #000;\n}\n.gui-modal__backdrop--off {\n  opacity: 0;\n  transition: opacity 0.15s linear;\n}\n.gui-modal__backdrop--on {\n  opacity: 0.5;\n}\n.gui-modal__dialog {\n  position: relative;\n  width: 40rem;\n  margin: 1.5rem auto;\n}\n.gui-modal__dialog--off {\n  opacity: 0;\n  transform: translateY(-2rem);\n  transition: opacity 0.15s linear, transform 0.15s linear;\n}\n.gui-modal__dialog--on {\n  opacity: 1;\n  transform: translate3d(0, 0, 0);\n}";
injector_0c888434(css$8,{"container":"body","singleTag":true,"prepend":true});

var measureScrollbarWidth = function () {
    var scrollbox = document.createElement('div');
    // eslint-disable-next-line fp/no-mutation
    scrollbox.style.overflow = 'scroll';
    document.body.appendChild(scrollbox);
    var scrollBarWidth = scrollbox.offsetWidth - scrollbox.clientWidth;
    document.body.removeChild(scrollbox);
    return scrollBarWidth;
};
var convertRemToPixels = function (rem) {
    if (/[\d.,]+rem/.test(rem)) {
        return parseInt(rem, 10) * parseFloat(getComputedStyle(document.documentElement).fontSize);
    }
    return parseInt(rem, 10);
};
var configureModal = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.prefix, prefix = _c === void 0 ? 'gui' : _c, classes = _b.classes;
    var Modal = function (_a) {
        var open = _a.open, children = _a.children, className = _a.className, onClose = _a.onClose;
        var bem = configureBem({ prefix: prefix, classes: classes });
        var b = bem('modal');
        var wrapper = useRef(null);
        var body = document.body;
        var originalBodyStyle = useState({
            paddingRight: body.style.paddingRight,
            overflow: body.style.overflow,
        })[0];
        var setPadding = function () {
            if (document.body.clientHeight > window.innerHeight) {
                // eslint-disable-next-line fp/no-mutation
                body.style.paddingRight = convertRemToPixels(originalBodyStyle.paddingRight) + measureScrollbarWidth() + "px";
                // eslint-disable-next-line fp/no-mutation
                body.style.overflow = 'hidden';
            }
        };
        var resetPadding = function () {
            // eslint-disable-next-line fp/no-mutation
            body.style.paddingRight = originalBodyStyle.paddingRight;
            // eslint-disable-next-line fp/no-mutation
            body.style.overflow = originalBodyStyle.overflow;
        };
        var onContainerClick = function (event) {
            if (event.target === wrapper.current) {
                onClose();
            }
        };
        return createPortal(React.createElement(Transition, { on: open, duration: 150, onMount: setPadding, onUnmount: resetPadding },
            React.createElement("div", { ref: wrapper, className: b('container', className), onClick: onContainerClick, role: "presentation" },
                React.createElement(Transition, { on: open, duration: 150, className: b('dialog') },
                    React.createElement("div", { className: b('dialog'), role: "dialog" }, children))),
            React.createElement(Transition, { on: open, duration: 150, className: b('backdrop') },
                React.createElement("div", { className: b('backdrop') }))), body);
    };
    return Modal;
};
var Modal = configureModal();

const css$9 = "/* stylelint-disable scss/dollar-variable-pattern */\n/* stylelint-enable scss/dollar-variable-pattern */\n.gui-table {\n  border-spacing: 0;\n  border-collapse: collapse;\n}\n.gui-table__head {\n  text-align: left;\n  padding: 0.25rem 0.5rem;\n  background-color: #f5f5f5;\n}\n.gui-table__head-content {\n  display: inline-flex;\n  min-height: 2.25rem;\n  align-items: center;\n}\n.gui-table__cell {\n  text-align: left;\n  padding: 0.5rem;\n  border-top: 1px solid #e0e0e0;\n}";
injector_0c888434(css$9,{"container":"body","singleTag":true,"prepend":true});

// This return type has to be inferred to keep the generic type param inside
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
var configureTable = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.prefix, prefix = _c === void 0 ? 'gui' : _c, classes = _b.classes;
    var bem = configureBem({ prefix: prefix, classes: classes });
    var b = bem('table');
    var DefaultCell = function (_a) {
        var value = _a.value;
        return (React.createElement("span", { className: b('cell-content') }, value));
    };
    var DefaultHead = function (_a) {
        var value = _a.value;
        return (React.createElement("span", { className: b('head-content') }, value));
    };
    var Table = function (_a) {
        var entries = _a.entries, columns = _a.columns, className = _a.className;
        return (React.createElement("table", { className: b([], className) },
            React.createElement("thead", null,
                React.createElement("tr", null, columns.map(function (_a) {
                    var title = _a.title, key = _a.key, _b = _a.head, HeadCell = _b === void 0 ? DefaultHead : _b;
                    return (React.createElement("th", { key: title, className: b('head') },
                        React.createElement(HeadCell, { index: -1, prop: key, value: title })));
                }))),
            React.createElement("tbody", null, entries.map(function (entry, index) { return (React.createElement("tr", { key: index }, columns.map(function (_a) {
                var title = _a.title, key = _a.key, _b = _a.cell, Cell = _b === void 0 ? DefaultCell : _b;
                return (React.createElement("td", { key: title, className: b('cell') },
                    React.createElement(Cell, { index: index, prop: key, value: entry[key] })));
            }))); }))));
    };
    return Table;
};
var Table = configureTable();

var DefaultLabel = function (_a) {
    var page = _a.page, pageSize = _a.pageSize, total = _a.total, className = _a.className, style = _a.style;
    return (React.createElement("span", { className: className, style: style },
        React.createElement("strong", null, "" + ((page - 1) * pageSize + 1)),
        " - ",
        React.createElement("strong", null, "" + Math.min(pageSize * page, total)),
        " von",
        ' ',
        React.createElement("strong", null, "" + total)));
};
var configurePagination = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.prefix, prefix = _c === void 0 ? 'gui' : _c, _d = _b.IconLeft, IconLeft = _d === void 0 ? ChevronLeftIcon : _d, _e = _b.IconRight, IconRight = _e === void 0 ? ChevronRightIcon : _e, _f = _b.Button, Button = _f === void 0 ? ClearButton : _f, _g = _b.Flex, Flex$1 = _g === void 0 ? Flex : _g, classes = _b.classes;
    var bem = configureBem({ prefix: prefix, classes: classes });
    var b = bem('pagination');
    var Pagination = function (_a) {
        var _b = _a.Label, Label = _b === void 0 ? DefaultLabel : _b, className = _a.className, setPage = _a.setPage, other = __rest(_a, ["Label", "className", "setPage"]);
        var page = other.page;
        var onPrevClick = function () {
            setPage(page - 1);
        };
        var onNextClick = function () {
            setPage(page + 1);
        };
        return (React.createElement(Flex$1, { className: b(), margin: "0.15rem" },
            React.createElement(Label, __assign({}, other, { className: b('label') })),
            React.createElement(Button, { iconLeft: IconLeft, onClick: onPrevClick }),
            React.createElement(Button, { iconLeft: IconRight, onClick: onNextClick })));
    };
    return Pagination;
};
var Pagination = configurePagination();

var wrapValidator = function (trigger, validator) { return function () {
    if (!validator || validator()) {
        return trigger();
    }
    return false;
}; };
var configureUsePopover = function (_a) {
    var _b = (_a === void 0 ? {} : _a).Popover, Popover$1 = _b === void 0 ? Popover : _b;
    var usePopover = function (content) {
        var _a = useState(false), isOpen = _a[0], setIsOpen = _a[1];
        var triggerId = useMemo(function () { return "trigger-" + Math.random().toString(36).substring(7); }, []);
        var trigger = useCallback(function (event) {
            setIsOpen(function (prevIsOpen) { return !prevIsOpen; });
            if (event) {
                event.stopPropagation();
            }
        }, [setIsOpen]);
        var close = useCallback(function () { return setIsOpen(false); }, [setIsOpen]);
        var PopoverComponent = useMemo(function () {
            var PopoverInternal = function (props) { return (React.createElement(Popover$1, __assign({}, props, { target: triggerId, open: isOpen, onClose: close }), content(close))); };
            return PopoverInternal;
        }, [close, content, isOpen, triggerId]);
        var TriggerComponent = useMemo(function () { return function (_a) {
            var renderTrigger = _a.renderTrigger, disabled = _a.disabled, validator = _a.validator;
            return renderTrigger({ trigger: wrapValidator(trigger, validator), disabled: disabled, id: triggerId });
        }; }, [trigger, triggerId]);
        return [PopoverComponent, TriggerComponent];
    };
    return usePopover;
};
/**
 * Hook that creates an EventSourceComponent that opens a Popover when triggered.
 * @param renderContent render function that returns a JSX.Element which will be used as popover content
 * @returns EventSourceComponent Opens the popover when triggered
 */
var usePopover = configureUsePopover();

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
var configureFilterButton = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.Button, Button$1 = _c === void 0 ? Button : _c, _d = _b.Popover, Popover$1 = _d === void 0 ? Popover : _d;
    var usePopover = configureUsePopover({ Popover: Popover$1 });
    var FilterButton = function (_) {
        var _a = usePopover(function (close) {
            var submit = function (event) {
                event.preventDefault();
                close();
            };
            return (React.createElement(Panel, { elevation: 2 },
                React.createElement("form", { onSubmit: submit },
                    React.createElement("input", null))));
        }), FilterPopover = _a[0], PopoverTrigger = _a[1];
        return (React.createElement(React.Fragment, null,
            React.createElement(PopoverTrigger, { renderTrigger: function (_a) {
                    var trigger = _a.trigger, id = _a.id;
                    return React.createElement(Button$1, { id: id, color: "clear", onClick: trigger, iconLeft: FilterIcon });
                } }),
            React.createElement(FilterPopover, null)));
    };
    return FilterButton;
};
var FilterButton = configureFilterButton();

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
var configureHead = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.prefix, prefix = _c === void 0 ? 'gui' : _c, _d = _b.FilterButton, FilterButton$1 = _d === void 0 ? FilterButton : _d, _e = _b.Flex, Flex$1 = _e === void 0 ? Flex : _e, _f = _b.Button, Button = _f === void 0 ? ClearButton : _f, classes = _b.classes;
    var bem = configureBem({ prefix: prefix, classes: classes });
    var b = bem('table');
    var Head = function (_a) {
        var value = _a.value, sort = _a.sort, onSort = _a.onSort, onFilter = _a.onFilter, className = _a.className, style = _a.style;
        var onSortClick = function () {
            onSort &&
                onSort(__assign(__assign({}, sort), { direction: sort.direction === 'asc' ? 'desc' : 'asc' }));
        };
        return (React.createElement("th", { className: b('head', className), style: style },
            React.createElement(Flex$1, { margin: "0.15rem" },
                onFilter && React.createElement(FilterButton$1, { onFilter: onFilter }),
                value,
                onSort && React.createElement(Button, { onClick: onSortClick, iconLeft: React.createElement(SortIcon, { size: "small" }) }))));
    };
    return Head;
};
var Head = configureHead();

/**
 * This a utility function that take a Button Component and returns an EventTriggerRenderer that can be used for the
 * renderTrigger prop of any EventTriggerComponent.
 * @param Button any Component that implements ButtonProps
 * @param props optional Button Props that should be applied to Button. Useful for `{type: 'submit'}` for example
 * @returns EventTriggerRenderer Function that can applied as is to renderTrigger of any EventTriggerComponent
 */
var createTriggerButton = function (Button, props) {
    if (props === void 0) { props = {}; }
    var TriggerRenderer = function (_a) {
        var trigger = _a.trigger, disabled = _a.disabled, loading = _a.loading, id = _a.id;
        var onClick = function (event) {
            if (props.type === 'submit') {
                event.preventDefault();
            }
            return trigger(event);
        };
        return React.createElement(Button, __assign({}, props, { onClick: onClick, disabled: disabled, id: id, loading: loading }));
    };
    return TriggerRenderer;
};

var effectTrigger = function (effect) {
    var Trigger = function (_a) {
        var input = _a.input, renderTrigger = _a.renderTrigger, disabled = _a.disabled, onSuccess = _a.onSuccess, onError = _a.onError;
        var _b = useState(false), loading = _b[0], setLoading = _b[1];
        var trigger = function () { return __awaiter(void 0, void 0, void 0, function () {
            var result, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setLoading(true);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, effect(input, {})];
                    case 2:
                        result = _a.sent();
                        setLoading(false);
                        if (onSuccess) {
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
                            onSuccess(result);
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _a.sent();
                        setLoading(false);
                        if (onError) {
                            onError(error_1);
                        }
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        return React.createElement(React.Fragment, null, renderTrigger({ trigger: trigger, loading: loading, disabled: disabled }));
    };
    return Trigger;
};
var effectTriggerContext = function (context) { return function (effect) {
    var TriggerWithContext = function (_a) {
        var input = _a.input, renderTrigger = _a.renderTrigger, disabled = _a.disabled, onSuccess = _a.onSuccess, onError = _a.onError;
        var contextValue = useContext(context);
        var _b = useState(false), loading = _b[0], setLoading = _b[1];
        var trigger = function () { return __awaiter(void 0, void 0, void 0, function () {
            var result, error_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setLoading(true);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, effect(input, contextValue)];
                    case 2:
                        result = _a.sent();
                        setLoading(false);
                        if (onSuccess) {
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
                            onSuccess(result);
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        error_2 = _a.sent();
                        setLoading(false);
                        if (onError) {
                            onError(error_2);
                        }
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        return React.createElement(React.Fragment, null, renderTrigger({ trigger: trigger, loading: loading, disabled: disabled }));
    };
    return TriggerWithContext;
}; };
/**
 * Hook that takes an Effect and returns an EffectTriggerComponent that can trigger the effect
 * @param effect any Effect as defined in @grapes-agency/universal
 * @returns EffectTriggerComponent See EffectTriggerComponent
 */
var useEffectTrigger = function (effect) { return effectTrigger(effect); };
/**
 * High order function that takes a React.Context and returns an useEffectTrigger hook that will apply the the value of
 * the React.Context as the Context argument of the triggered effect. See @grapes-agency/universal for information on
 * effect arguments
 * @param context Any React.Context
 * @returns useEffectTrigger hook, see useEffectTrigger
 */
var createContextTrigger = function (context) {
    var partial = effectTriggerContext(context);
    return function (effect) { return useMemo(function () { return partial(effect); }, [effect]); };
};

/**
 * Utility hook that takes an EffectTriggerComponent and props to return an EventTriggerComponent that will apply
 * supplied props. Useful if you want to turn an EffectTriggerComponent into a more general EventTriggerComponent that
 * satisfy another interfaces.
 *
 * @param Trigger The EffectTriggerComponent
 * @param props EffectTriggerProps that should be applied to the EffectTriggerComponent except renderTrigger
 * @returns EventTriggerComponent An EventTriggerComponent
 */
var useConnectEffectTrigger = function (Trigger, props) {
    var TriggerComponent = function (_a) {
        var renderTrigger = _a.renderTrigger, disabled = _a.disabled;
        return (React.createElement(Trigger, __assign({}, props, { disabled: props.disabled || disabled, renderTrigger: renderTrigger })));
    };
    return TriggerComponent;
};

var wrapValidator$1 = function (trigger, validator) { return function () {
    if (!validator || validator()) {
        return trigger();
    }
    return false;
}; };
/**
 * Takes a content render function that returns a JSX.Element and returns a ModalComponent and an EventSourceComponent that
 * will open the rendered content in a modal when the Event is triggered. This is used to separate the modal from the
 * EventSource, so the EventSource can be unmounted while the modal is open (for example when the modal is opened
 * through a PopoverMenu
 * @param renderContent render function that returns a JSX.Element which will be used as modal content
 * @returns Array first element is the ModalComponent. Second
 * element is EventSourceComponent to trigger open state of modal
 */
var useModal = function (content) {
    var _a = useState(false), open = _a[0], setOpen = _a[1];
    var trigger = function () {
        setOpen(!open);
        return !open;
    };
    var ModalComponent = function () { return (React.createElement(Modal, { open: open, onClose: trigger }, content(trigger))); };
    var TriggerComponent = function (_a) {
        var renderTrigger = _a.renderTrigger, disabled = _a.disabled, validator = _a.validator;
        return renderTrigger({ trigger: wrapValidator$1(trigger, validator), disabled: disabled });
    };
    return [ModalComponent, TriggerComponent];
};

export { AngleDownIcon, BugIcon, Button, ChevronLeftIcon, ChevronRightIcon, ClearButton, ComponentOrElement, FilterButton, FilterIcon, Flex, FlexIgnore, Head, Icon, Input, Modal, Pagination, Panel, Popover, PrimaryButton, SecondaryButton, Select, SortAscIcon, SortDescIcon, SortIcon, Spinner, Table, Transition, applyProps, applyPropsRef, bem, configureBem, configureButton, configureFilterButton, configureFlex, configureHead, configureIcon, configureInput, configureModal, configurePagination, configurePanel, configurePopover, configureSelect, configureSpinner, configureTable, configureUsePopover, createContextTrigger, createTriggerButton, ensureEndsWith, escapeRegExp, randomString, useConnectEffectTrigger, useEffectTrigger, useModal, usePopover };
